import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Toast — notificacion individual (DESIGN-SYSTEM §3.10).
 *
 * Variantes semanticas con icono + color:
 *  - info     -> turquesa  (`teal-700`)   role="status"  aria-live="polite"
 *  - success  -> verde     (`green-700`)  role="status"  aria-live="polite"
 *  - warning  -> dorado    (`gold-300`)   role="alert"   aria-live="assertive"
 *  - error    -> rojo      (`error`)      role="alert"   aria-live="assertive"
 *
 * El componente es controlado por `ToastProvider` (no se monta solo). Recibe
 * `item` y `onDismiss`. La animacion entra desde la derecha; en reduced-motion
 * solo hace fade.
 */
export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration: number | null;
  action?: { label: string; onClick: () => void };
}

export interface ToastProps {
  item: ToastItem;
  onDismiss: () => void;
}

// Mapa de estilos por variante (accent bar lateral + fondo cream).
const variantAccent: Record<ToastVariant, string> = {
  info: 'before:bg-teal-700',
  success: 'before:bg-green-700',
  warning: 'before:bg-gold-300',
  error: 'before:bg-error',
};

const variantIconColor: Record<ToastVariant, string> = {
  info: 'text-teal-700',
  success: 'text-green-700',
  warning: 'text-gold-300',
  error: 'text-error',
};

function VariantIcon({ variant }: { variant: ToastVariant }) {
  // Iconos lucide consistentes con la familia tecnica del DS §7.3.
  switch (variant) {
    case 'success':
      return <CheckCircle2 className="h-5 w-5" aria-hidden="true" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5" aria-hidden="true" />;
    case 'error':
      return <AlertCircle className="h-5 w-5" aria-hidden="true" />;
    case 'info':
    default:
      return <Info className="h-5 w-5" aria-hidden="true" />;
  }
}

export function Toast({ item, onDismiss }: ToastProps) {
  const reduceMotion = useReducedMotion();

  // role/aria-live por variante (DESIGN-SYSTEM §8.2).
  const isAssertive = item.variant === 'warning' || item.variant === 'error';
  const role = isAssertive ? 'alert' : 'status';
  const ariaLive = isAssertive ? 'assertive' : 'polite';

  // Slide+fade desde la derecha. Reduced-motion neutraliza el translate.
  const variants: Variants = reduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      }
    : {
        initial: { opacity: 0, x: 24, scale: 0.98 },
        animate: {
          opacity: 1,
          x: 0,
          scale: 1,
          transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] as const },
        },
        exit: {
          opacity: 0,
          x: 12,
          transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const },
        },
      };

  return (
    <motion.div
      layout
      role={role}
      aria-live={ariaLive}
      aria-atomic="true"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      className={cn(
        // Pointer events: re-habilitamos (el viewport los desactiva).
        'pointer-events-auto w-full',
        'relative bg-cream-50 text-primary-900',
        'rounded-md shadow-elev2 border border-cream-200',
        'overflow-hidden',
        // Barra lateral de color via pseudo-elemento.
        'before:content-[""] before:absolute before:inset-y-0 before:left-0 before:w-1',
        variantAccent[item.variant],
      )}
    >
      <div className="flex items-start gap-3 p-4 pl-5">
        <span className={cn('mt-0.5 shrink-0', variantIconColor[item.variant])}>
          <VariantIcon variant={item.variant} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-h4 font-body font-semibold text-primary-900">{item.title}</p>
          {item.description && (
            <p className="mt-1 text-caption text-primary-900/70">{item.description}</p>
          )}
          {item.action && (
            <button
              type="button"
              onClick={() => {
                item.action?.onClick();
                onDismiss();
              }}
              className={cn(
                'mt-2 inline-flex items-center text-button uppercase tracking-[0.04em]',
                'text-primary-700 hover:text-primary-800',
                'transition-colors duration-2 ease-out',
              )}
            >
              {item.action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar notificacion"
          className={cn(
            // Touch target >= 44x44 (WCAG 2.5.5). El icono mantiene 16px por
            // alineacion optica con el resto del toast.
            'shrink-0 inline-flex items-center justify-center',
            'h-11 w-11 -m-2 rounded text-primary-900',
            'hover:bg-cream-200 transition-colors duration-2 ease-out',
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </motion.div>
  );
}
